import { useState, useEffect } from 'react';
import { X, ShoppingCart } from 'lucide-react';
import { useSticker } from '../context/StickerContext';
import { usePrice } from '../hooks/usePrice';
import { Button } from './ui/button';
import { Label } from './ui/label';
import NumberInput from './NumberInput';
import toast from 'react-hot-toast';
import { initiateCheckout, uploadDesign } from '../utils/checkout';
import { getCanvasDataURL } from '../utils/canvas/exportHandlers';
import { useAnalytics } from '../context/AnalyticsContext';
import { trackEvents, trackStickerDesign, trackError } from '../utils/analytics';
import { fabric } from 'fabric';
import { createClipPath } from '../utils/shapes';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '../utils/cn';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Maximize2 } from 'lucide-react';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CheckoutModal({ isOpen, onClose }: CheckoutModalProps) {
  const { 
    size, 
    quantity, 
    updateQuantity,
    finish,
    updateFinish,
    canvasShape,
    designName,
  } = useSticker();
  const { totalPrice, pricePerSticker, validQuantity } = usePrice();
  const { analytics } = useAnalytics();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [customerDetails, setCustomerDetails] = useState({
    email: '',
  });
  const [errors, setErrors] = useState({
    email: '',
  });
  const [isPreviewEnlarged, setIsPreviewEnlarged] = useState(false);

  const validateForm = () => {
    const newErrors = {
      email: '',
    };

    // Quantity validation
    if (quantity < 50) {
      toast.error('Minimum quantity is 50 stickers');
      updateQuantity(50);
      return false;
    }
    if (quantity > 10000) {
      toast.error('Maximum quantity is 10,000 stickers');
      updateQuantity(10000);
      return false;
    }

    // Email validation
    if (!customerDetails.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(customerDetails.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  useEffect(() => {
    if (quantity < 50) {
      updateQuantity(50);
    }
    if (quantity > 10000) {
      updateQuantity(10000);
    }
  }, []);

  const generatePreview = async () => {
    if (isGeneratingPreview) return;
    
    try {
      setIsGeneratingPreview(true);
      console.log('Starting preview generation...');
      
      const canvas = (window as any).fabricCanvas as fabric.Canvas;
      if (!canvas) {
        throw new Error('Canvas not found');
      }

      // Ensure the shape is applied before generating preview
      if (canvasShape && !canvas.clipPath) {
        const clipPath = createClipPath(canvasShape, canvas.width!, canvas.height!);
        if (clipPath) {
          canvas.clipPath = clipPath;
          canvas.renderAll();
        }
      }

      // Wait a frame to ensure canvas is rendered
      await new Promise(resolve => requestAnimationFrame(resolve));

      console.log('Generating canvas data URL...');
      const dataUrl = await getCanvasDataURL(canvas);
      console.log('Preview URL format check:', {
        startsWithDataImage: dataUrl.startsWith('data:image/'),
        length: dataUrl.length,
        prefix: dataUrl.substring(0, 30) + '...'
      });
      
      // Validate the data URL
      if (!dataUrl.startsWith('data:image/')) {
        throw new Error('Invalid preview image generated');
      }

      setPreviewUrl(dataUrl);
    } catch (error) {
      console.error('Failed to generate preview:', error);
      toast.error('Failed to generate preview. Please try again.');
      setPreviewUrl('');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handlePurchase = async () => {
    if (isProcessing) return;

    if (!validateForm()) {
      return;
    }

    // Signal that checkout is intentionally starting
    if ((window as any).handleCheckoutStart) {
      (window as any).handleCheckoutStart();
    }

    try {
      setIsProcessing(true);
      console.log('Starting purchase process...');

      // Generate preview if not already generated
      if (!previewUrl) {
        console.log('No preview URL, generating...');
        await generatePreview();
      }

      // Verify we have a valid preview
      if (!previewUrl) {
        throw new Error('Failed to generate design preview');
      }

      console.log('Upload data format check:', {
        startsWithDataImage: previewUrl.startsWith('data:image/'),
        length: previewUrl.length,
        prefix: previewUrl.substring(0, 30) + '...'
      });

      // First upload the design to R2
      const finalDesignUrl = await uploadDesign({
        imageData: previewUrl,
        shape: canvasShape === 'square' ? 'rect' : canvasShape, // Normalize square to rect
        size: {
          width: size.width,
          height: size.height
        },
        title: designName || 'Custom Sticker',
        quantity
      });

      if (!finalDesignUrl) {
        throw new Error('Failed to upload design');
      }

      console.log('Design uploaded successfully, URL:', finalDesignUrl);

      trackStickerDesign(analytics, trackEvents.CHECKOUT_STARTED, {
        design_name: designName || 'Custom Sticker',
        width: size.width,
        height: size.height,
        shape: canvasShape,
        finish,
        quantity,
        price: totalPrice,
        price_per_sticker: pricePerSticker,
        customer_email: customerDetails.email
      });

      // Format shape name for display
      let displayShape = canvasShape;
      if (displayShape === 'square' || displayShape === 'rect' || displayShape === 'rectangle') {
        displayShape = 'Square/Rectangle';
      } else {
        displayShape = displayShape.charAt(0).toUpperCase() + displayShape.slice(1);
      }

      const checkoutItem = {
        name: designName || 'Custom Die Cut Sticker',
        price: totalPrice,
        quantity: quantity,
        size: `${size.width}" × ${size.height}"`,
        finish: finish,
        shape: displayShape,
        image_url: finalDesignUrl,
        customer_email: customerDetails.email,
      };

      await initiateCheckout([checkoutItem]);
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Failed to start checkout. Please try again.');
      
      if (err instanceof Error) {
        trackError(analytics, err, {
          context: 'checkout',
          design_name: designName,
          width: size.width,
          height: size.height,
          shape: canvasShape,
          finish,
          quantity,
          price: totalPrice
        });
      }
      
      trackStickerDesign(analytics, trackEvents.CHECKOUT_ABANDONED, {
        design_name: designName || 'Custom Sticker',
        width: size.width,
        height: size.height,
        shape: canvasShape,
        finish,
        quantity,
        price: totalPrice,
        price_per_sticker: pricePerSticker,
        customer_email: customerDetails.email,
        error: err instanceof Error ? err.message : 'Unknown error',
        error_type: err instanceof Error ? err.name : 'UnknownError',
        abandonment_reason: 'error',
        abandonment_step: 'payment_initiation',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setPreviewUrl('');
    onClose();
  };

  const handleQuantityChange = (value: number) => {
    updateQuantity(value);
    
    trackStickerDesign(analytics, trackEvents.ORDER_QUANTITY_CHANGED, {
      design_name: designName,
      width: size.width,
      height: size.height,
      shape: canvasShape,
      finish,
      quantity: value,
      price: totalPrice,
      price_per_sticker: pricePerSticker
    });
  };

  const handleQuantityBlur = (value: number) => {
    if (value < 50) {
      updateQuantity(50);
      toast.error('The minimum sticker quantity is 50', {
        duration: 4000,
        position: 'bottom-center',
      });
      trackStickerDesign(analytics, trackEvents.ORDER_QUANTITY_CHANGED, {
        design_name: designName,
        width: size.width,
        height: size.height,
        shape: canvasShape,
        finish,
        quantity: 50,
        price: totalPrice,
        price_per_sticker: pricePerSticker,
        validation_error: 'below_minimum'
      });
    } else if (value > 10000) {
      updateQuantity(10000);
      toast.error('The maximum sticker quantity is 10,000', {
        duration: 4000,
        position: 'bottom-center',
      });
      trackStickerDesign(analytics, trackEvents.ORDER_QUANTITY_CHANGED, {
        design_name: designName,
        width: size.width,
        height: size.height,
        shape: canvasShape,
        finish,
        quantity: 10000,
        price: totalPrice,
        price_per_sticker: pricePerSticker,
        validation_error: 'above_maximum'
      });
    }
  };

  const handleFinishChange = (newFinish: 'matte' | 'glossy') => {
    updateFinish(newFinish);
    trackStickerDesign(analytics, trackEvents.DESIGN_REFINISHED, {
      design_name: designName,
      width: size.width,
      height: size.height,
      shape: canvasShape,
      finish: newFinish,
      quantity,
      price: totalPrice,
      price_per_sticker: pricePerSticker
    });
  };

  useEffect(() => {
    if (isOpen) {
      generatePreview();
    }
  }, [isOpen]);

  // Track checkout abandonment when user navigates away
  useEffect(() => {
    if (!isOpen) return;

    // Flag to track if checkout is in progress
    let isCheckoutInProgress = false;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Don't trigger when checkout is intentionally in progress
      if (isCheckoutInProgress) return;
      
      // Track abandonment when user closes the tab/browser during checkout
      trackStickerDesign(analytics, trackEvents.CHECKOUT_ABANDONED, {
        design_name: designName || 'Custom Sticker',
        width: size.width,
        height: size.height,
        shape: canvasShape,
        finish,
        quantity,
        price: totalPrice,
        price_per_sticker: pricePerSticker,
        customer_email: customerDetails.email || undefined,
        abandonment_reason: 'browser_closed',
        abandonment_step: 'checkout_form',
        timestamp: new Date().toISOString()
      });
      
      // Standard beforeunload behavior
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    // Patch the handlePurchase function to set the flag
    const originalHandlePurchase = handlePurchase;
    (window as any).handleCheckoutStart = () => {
      isCheckoutInProgress = true;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      (window as any).handleCheckoutStart = undefined;
    };
  }, [isOpen, analytics, designName, size, canvasShape, finish, quantity, totalPrice, pricePerSticker, customerDetails]);

  if (!isOpen) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={() => handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay 
          className="
            fixed inset-0 bg-black/60 backdrop-blur-sm
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
            z-50
          "
          onClick={handleClose}
        />
        <Dialog.Content
          className="
            fixed left-[50%] top-[50%] z-[60]
            w-full max-w-[95%] md:max-w-[800px] max-h-[90vh]
            translate-x-[-50%] translate-y-[-50%]
            bg-background rounded-lg shadow-xl
            overflow-y-auto
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
            data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
            data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]
            data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]
          "
          aria-describedby="checkout-description"
        >
          <Dialog.Title className="sr-only">Checkout</Dialog.Title>
          <VisuallyHidden>
            <Dialog.Description id="checkout-description">
              Complete your sticker order by providing your email and confirming your design details.
            </Dialog.Description>
          </VisuallyHidden>
          {/* Drag Handle */}
          <div className="w-12 h-1 mx-auto mt-3 mb-2 bg-muted-foreground/10 rounded-full md:hidden" />

          {/* Header */}
          <div className="relative px-4 py-4 md:px-6 md:py-5 border-b border-border/10">
            <div className="flex items-center justify-between h-8 md:h-9">
              <div className="flex items-center gap-2.5 md:gap-3">
                <div className="flex items-center justify-center h-8 w-8 md:h-9 md:w-9 rounded-full bg-primary/10">
                  <ShoppingCart className="h-[18px] w-[18px] md:h-5 md:w-5 text-primary" />
                </div>
                <span className="text-base md:text-lg font-semibold leading-none">Checkout</span>
              </div>
              
              <Dialog.Close className="flex items-center justify-center h-8 w-8 md:h-9 md:w-9 rounded-full hover:bg-muted/80 transition-colors" onClick={handleClose}>
                <X className="h-[18px] w-[18px] md:h-5 md:w-5" />
                <span className="sr-only">Close</span>
              </Dialog.Close>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto">
            <div className="p-4 md:p-5 space-y-4 md:space-y-5">
              {/* Preview and Basic Details Section */}
              <div className="flex gap-3 md:gap-4">
                {/* Preview */}
                <div className="relative w-[100px] md:w-[140px] shrink-0" onClick={() => setIsPreviewEnlarged(!isPreviewEnlarged)}>
                  <div className="relative bg-background rounded-lg md:rounded-xl p-2 md:p-3 flex items-center justify-center border border-border/50">
                    <div className={cn(
                      "relative bg-white rounded-lg shadow-sm overflow-hidden transition-all aspect-square",
                      isPreviewEnlarged ? "fixed inset-4 z-50 p-4" : "w-full"
                    )}>
                      {isGeneratingPreview ? (
                        <div className="aspect-square animate-pulse bg-muted" />
                      ) : (
                        <img
                          src={previewUrl}
                          alt="Sticker Preview"
                          className="w-full h-full object-contain"
                        />
                      )}
                      <div className="absolute bottom-1.5 right-1.5 p-1.5 rounded-full bg-background/80 text-xs">
                        <Maximize2 className="h-3 w-3 md:h-4 md:w-4" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Basic Details */}
                <div className="flex-1 flex flex-col justify-center min-h-[100px] md:min-h-[140px]">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm md:text-base">Size</span>
                      <span className="font-medium text-sm md:text-base">{size.width}" × {size.height}"</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm md:text-base">Shape</span>
                      <span className="font-medium text-sm md:text-base capitalize">{canvasShape}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Configuration */}
              <div className="space-y-4">
                {/* Finish Selection */}
                <div className="space-y-2">
                  <Label className="text-sm md:text-base">Finish</Label>
                  <div className="grid grid-cols-2 gap-2 md:gap-3">
                    {["matte", "glossy"].map((type) => (
                      <button
                        key={type}
                        onClick={() => handleFinishChange(type)}
                        className={cn(
                          "px-3 py-2 md:px-4 md:py-2 rounded-xl border-2 transition-all duration-200 text-sm md:text-base",
                          finish === type
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-border/50 hover:border-primary/50"
                        )}
                      >
                        <span className="font-medium capitalize">{type}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quantity and Email */}
                <div className="space-y-2">
                  <Label htmlFor="quantity" className="text-sm md:text-base">Quantity</Label>
                  <NumberInput
                    value={quantity}
                    onChange={handleQuantityChange}
                    onBlur={handleQuantityBlur}
                    min={50}
                    max={10000}
                    label="Quantity"
                    minErrorMessage="The minimum sticker quantity is 50"
                    maxErrorMessage="The maximum sticker quantity is 10,000"
                    className="h-10 md:h-11 rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm md:text-base">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <input
                    type="email"
                    id="email"
                    value={customerDetails.email}
                    onChange={(e) =>
                      setCustomerDetails({ ...customerDetails, email: e.target.value })
                    }
                    className="h-10 md:h-11 rounded-xl w-full px-3 md:px-4 border border-input"
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="text-xs md:text-sm text-destructive mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Price Summary */}
                <div className="rounded-xl bg-gradient-to-b from-muted/50 to-muted p-3 md:p-4 space-y-2">
                  <div className="flex justify-between text-sm md:text-base">
                    <span className="text-muted-foreground">Price per sticker</span>
                    <span className="font-medium">${pricePerSticker.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-base md:text-lg font-semibold">
                    <span>Total</span>
                    <span>${totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 p-4 md:p-5 bg-gradient-to-t from-background via-background to-background/0">
            <Button
              className="w-full h-11 md:h-12 rounded-xl text-base md:text-lg font-medium"
              onClick={handlePurchase}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span>Processing...</span>
                </div>
              ) : (
                <>
                  <ShoppingCart className="mr-2 h-4 w-4" />
                  Proceed to Checkout
                </>
              )}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}